import { useEffect } from 'react'
import { useGetBagStatus } from './backend/bagsPage'
import { useGetSeasonalAlerts } from './backend/inventoryPage'
import { useGetTierReviewStatus } from './backend/tierReview'
import { useGetInvoiceProblems } from './backend/scanPage'
import { BAG_STATUS_CHANGED, type BagSizeStatus, type BagStatus } from '../utils/bagStatus'
import { TIER_REVIEW_CHANGED, type TierReviewStatus } from '../utils/tierReview'

// Fired on window after failed Zoho invoice pushes are retried or dismissed.
export const INVOICE_PROBLEMS_CHANGED = 'hub:invoice-problems-changed'

export interface InvoiceProblem {
  line_id: string
  order_id: string
  customer: string | null
  item_name: string
  kind: 'invoice' | 'order'
  error: string
  failed_at: string
}

export type SeasonalEvent = 'start' | 'end'

export interface SeasonalAlert {
  product: string
  event: SeasonalEvent
  occurs_on: string
  days_ago: number
  suggested_action: 'publish' | 'unpublish'
}

// Fired on window after a seasonal alert is acted on or dismissed, so the
// nav link's count catches up.
export const SEASONAL_ALERTS_CHANGED = 'hub:seasonal-alerts-changed'

interface SeasonalAlertsData {
  today: string
  alerts: SeasonalAlert[]
}

// Alerts that used to show as banners under the header: bag sizes at their
// reorder point and seasonal products whose season just started or ended,
// plus a quarterly tier review with changes waiting for a decision, and order
// lines whose Zoho invoice push failed.
// The Dashboard's Today section lists them, and the Dashboard nav link shows
// how many there are.
export function useHubAlerts() {
  const getBagStatus = useGetBagStatus()
  const getSeasonalAlerts = useGetSeasonalAlerts()
  const getTierReviewStatus = useGetTierReviewStatus()
  const getInvoiceProblems = useGetInvoiceProblems()

  useEffect(() => {
    void getBagStatus.trigger({})
    void getSeasonalAlerts.trigger({})
    void getTierReviewStatus.trigger({})
    void getInvoiceProblems.trigger({}, { skipCache: true })
    const refreshInvoices = () => void getInvoiceProblems.trigger({}, { skipCache: true })
    const refreshBags = () => void getBagStatus.trigger({}, { skipCache: true })
    const refreshTiers = () => void getTierReviewStatus.trigger({}, { skipCache: true })
    window.addEventListener(BAG_STATUS_CHANGED, refreshBags)
    window.addEventListener(SEASONAL_ALERTS_CHANGED, refresh)
    window.addEventListener(TIER_REVIEW_CHANGED, refreshTiers)
    window.addEventListener(INVOICE_PROBLEMS_CHANGED, refreshInvoices)
    return () => {
      window.removeEventListener(BAG_STATUS_CHANGED, refreshBags)
      window.removeEventListener(SEASONAL_ALERTS_CHANGED, refresh)
      window.removeEventListener(TIER_REVIEW_CHANGED, refreshTiers)
      window.removeEventListener(INVOICE_PROBLEMS_CHANGED, refreshInvoices)
    }
    function refresh() {
      void getSeasonalAlerts.trigger({}, { skipCache: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const bagStatus = getBagStatus.data as BagStatus | null | undefined
  const lowBags: BagSizeStatus[] = (bagStatus?.sizes ?? []).filter(s => s.needs_reorder)
  const seasonal = ((getSeasonalAlerts.data as SeasonalAlertsData | null | undefined)?.alerts ?? [])
  const tierStatus = getTierReviewStatus.data as TierReviewStatus | null | undefined
  const tierReview = tierStatus && tierStatus.pending > 0 ? tierStatus : null
  const invoiceProblems =
    (getInvoiceProblems.data as { problems?: InvoiceProblem[] } | null | undefined)?.problems ?? []

  return {
    lowBags,
    seasonal,
    tierReview,
    invoiceProblems,
    loading: (getBagStatus.loading && !bagStatus) || (getSeasonalAlerts.loading && !getSeasonalAlerts.data),
    count: lowBags.length + seasonal.length + (tierReview ? 1 : 0) + (invoiceProblems.length ? 1 : 0),
  }
}
